// Basic Analytics Tracking for Plugged In
class Analytics {
  constructor() {
    this.sessionId = this.generateSessionId();
    this.userId = null;
    this.events = [];
    this.startTime = Date.now();
    
    // Track page load
    this.track('page_load', {
      url: window.location.href,
      referrer: document.referrer,
      timestamp: new Date().toISOString()
    });
    
    // Track page unload
    window.addEventListener('beforeunload', () => {
      this.track('session_end', {
        duration: Date.now() - this.startTime,
        events_count: this.events.length
      });
      this.flush();
    });
  }

  generateSessionId() {
    return 'sess_' + Math.random().toString(36).substr(2, 9) + '_' + Date.now();
  }

  setUser(userId, userInfo = {}) {
    this.userId = userId;
    this.track('user_identified', {
      user_id: userId,
      ...userInfo
    });
  }

  track(eventName, properties = {}) {
    const event = {
      event: eventName,
      session_id: this.sessionId,
      user_id: this.userId,
      timestamp: new Date().toISOString(),
      url: window.location.href,
      user_agent: navigator.userAgent,
      screen: {
        width: window.screen.width,
        height: window.screen.height
      },
      viewport: {
        width: window.innerWidth,
        height: window.innerHeight
      },
      properties
    };

    this.events.push(event);
    
    // Auto-flush every 10 events or every 30 seconds
    if (this.events.length >= 10) {
      this.flush();
    }
    
    console.log('Analytics Event:', eventName, properties);
  }

  async flush() {
    if (this.events.length === 0) return;

    const eventsToSend = [...this.events];
    this.events = [];

    try {
      // Send to our analytics endpoint
      await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/analytics/events`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(localStorage.getItem('access_token') && {
            'Authorization': `Bearer ${localStorage.getItem('access_token')}`
          })
        },
        body: JSON.stringify({
          events: eventsToSend
        })
      });
    } catch (error) {
      console.error('Failed to send analytics events:', error);
      // Re-add events to queue on failure
      this.events.unshift(...eventsToSend);
    }
  }

  // User Actions
  trackSignup(method = 'email') {
    this.track('user_signup', { method });
  }

  trackLogin(method = 'email') {
    this.track('user_login', { method });
  }

  trackLogout() {
    this.track('user_logout');
  }

  // Friend Actions
  trackFriendAdded(friendData) {
    this.track('friend_added', {
      services_count: friendData.services?.length || 0,
      has_email: !!friendData.email,
      has_phone: !!friendData.phone,
      has_company: !!friendData.company
    });
  }

  trackFriendViewed(friendId) {
    this.track('friend_viewed', { friend_id: friendId });
  }

  trackSearch(query, results_count) {
    this.track('search_performed', {
      query: query?.substring(0, 100), // Limit query length for privacy
      results_count
    });
  }

  trackServiceFilter(service) {
    this.track('service_filtered', { service });
  }

  // Contact Actions
  trackCall(friendId) {
    this.track('friend_called', { friend_id: friendId });
  }

  trackEmail(friendId) {
    this.track('friend_emailed', { friend_id: friendId });
  }

  // Review & Rating Actions
  trackReviewSubmitted(friendId, rating) {
    this.track('review_submitted', {
      friend_id: friendId,
      rating
    });
  }

  trackPlugTracked(friendId) {
    this.track('plug_tracked', { friend_id: friendId });
  }

  // Sharing Actions
  trackShareInitiated(friends_count, share_type) {
    this.track('share_initiated', {
      friends_count,
      share_type
    });
  }

  trackReferralGenerated(friendId, service) {
    this.track('referral_generated', {
      friend_id: friendId,
      service
    });
  }

  // Conversion Events
  trackSubscriptionUpgrade(plan) {
    this.track('subscription_upgraded', { plan });
  }

  trackFeatureUsed(feature_name) {
    this.track('feature_used', { feature: feature_name });
  }

  // Error Tracking
  trackError(error, context = {}) {
    this.track('error_occurred', {
      error_message: error.message,
      error_stack: error.stack?.substring(0, 500),
      ...context
    });
  }
}

// Create global analytics instance
const analytics = new Analytics();

// Auto-flush every 30 seconds
setInterval(() => {
  analytics.flush();
}, 30000);

export default analytics;